import { useRef, useState } from 'react';
import { dimensionsReduites, reduitLImage } from '../lib/image';
import { C } from '../theme/palette';

/** Taille maximale d'une photo, en octets. Le serveur applique la même limite. */
export const TAILLE_MAX_PHOTO = 10 * 1024 * 1024;

/**
 * Côté le plus long d'une photo de chantier, en pixels.
 *
 * <p>Une photo de téléphone pèse 3 à 8 Mo pour 4000 pixels de large ; gardée
 * sept ans pour chaque chantier, elle remplirait le stockage. À 1600 pixels,
 * une fissure ou un raccord se lisent encore, pour 300 à 500 Ko.
 */
export const COTE_PHOTO = 1600;

/** Qualité du JPEG d'une photo de chantier. */
const QUALITE_PHOTO = 0.8;

/** Exprime une taille en mégaoctets, à une décimale. */
const enMo = (octets) => (octets / (1024 * 1024)).toFixed(1).replace('.', ',');

/**
 * Bouton de prise de photo, par la caméra ou depuis les fichiers.
 *
 * @param {object} props propriétés du composant
 * @param {string} [props.label] libellé du bouton
 * @param {string} [props.color] couleur d'accent
 * @param {boolean} [props.full] occupe toute la largeur
 * @param {(apercu: string, fichier: Blob) => void} props.onPhoto reçoit un
 *   aperçu affichable et le fichier lui-même, à envoyer tel quel
 * @param {number} [props.cote] côté le plus long gardé ; un plan, plus fin, en garde davantage
 * @returns {import('react').ReactElement} le bouton et son écran de prise de vue
 */
export function PhotoBtn({ label, color, full, onPhoto, cote = COTE_PHOTO }) {
  const col = color || C.teal;
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(false);
  const [trop, setTrop] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const stop = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch (e) {}
    setOpen(false);
    setErr(false);
    setTrop('');
  };
  const start = async () => {
    setOpen(true);
    setErr(false);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        // Une photo qui peut servir de preuve : la meilleure définition que
        // l'appareil donne, au lieu des 640 × 480 proposés par défaut.
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch (e) {
      setErr(true);
    }
  };
  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement('canvas');
    const { largeur, hauteur } = dimensionsReduites(
      v.videoWidth || 720,
      v.videoHeight || 540,
      cote,
    );
    c.width = largeur;
    c.height = hauteur;
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    const data = c.toDataURL('image/jpeg', QUALITE_PHOTO);
    // Le fichier accompagne l'aperçu : les écrans qui le persistent l'envoient
    // tel quel, les autres se contentent de l'aperçu comme auparavant.
    c.toBlob(
      (blob) => {
        onPhoto && onPhoto(data, blob);
        stop();
      },
      'image/jpeg',
      QUALITE_PHOTO,
    );
  };
  const fromFile = async (e) => {
    const original = e.target.files && e.target.files[0];
    if (!original) return;
    const champ = e.target;
    // La photo de la galerie est réduite avant l'envoi, comme celle de la
    // caméra : la pleine définition du téléphone pèserait dix fois plus,
    // gardée sept ans, pour un détail que l'écran ne montre pas.
    const f = await reduitLImage(original, cote);
    if (f.size > TAILLE_MAX_PHOTO) {
      setTrop(
        `Cette photo pèse ${enMo(f.size)} Mo, au-delà des ${enMo(TAILLE_MAX_PHOTO)} Mo admis.`,
      );
      champ.value = '';
      return;
    }
    setTrop('');
    const r = new FileReader();
    r.onload = () => {
      onPhoto && onPhoto(r.result, f);
      stop();
    };
    r.readAsDataURL(f);
  };
  return (
    <>
      <div
        onClick={start}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          border: '1px dashed ' + col,
          borderRadius: 9,
          padding: '14px',
          fontSize: 11,
          fontWeight: 700,
          color: col,
          cursor: 'pointer',
          width: full ? '100%' : 'auto',
          boxSizing: 'border-box',
        }}
      >
        {label || '📷 Prendre une photo'}
      </div>
      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.92)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            paddingTop: 'max(16px, var(--st-haut))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          }}
        >
          {!err ? (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                style={{ width: '100%', maxWidth: 440, borderRadius: 14, background: '#000' }}
              />
              <div style={{ display: 'flex', gap: 12, marginTop: 18, alignItems: 'center' }}>
                <button
                  onClick={stop}
                  style={{
                    background: 'rgba(255,255,255,.15)',
                    border: 'none',
                    color: '#fff',
                    borderRadius: 99,
                    padding: '11px 18px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={snap}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: '#fff',
                    border: '4px solid ' + col,
                    cursor: 'pointer',
                    fontSize: 22,
                  }}
                >
                  📸
                </button>
                <label
                  style={{
                    background: 'rgba(255,255,255,.15)',
                    color: '#fff',
                    borderRadius: 99,
                    padding: '11px 18px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Galerie
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={fromFile}
                  />
                </label>
              </div>
              <div style={{ color: 'rgba(255,255,255,.6)', fontSize: 11, marginTop: 12 }}>
                Cadrez puis touchez 📸 pour prendre la photo
              </div>
              {trop && (
                <div
                  role="alert"
                  style={{
                    marginTop: 10,
                    color: '#FFD5D9',
                    background: 'rgba(214,40,57,.25)',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 11,
                    maxWidth: 340,
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}
                >
                  {trop}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', maxWidth: 320 }}>
              <div style={{ color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                Caméra indisponible
              </div>
              <div
                style={{
                  color: 'rgba(255,255,255,.65)',
                  fontSize: 11,
                  lineHeight: 1.6,
                  marginBottom: 16,
                }}
              >
                Autorise l'accès à la caméra, ou importe une photo depuis ton appareil.
              </div>
              {trop && (
                <div
                  role="alert"
                  style={{
                    color: '#FFD5D9',
                    background: 'rgba(214,40,57,.25)',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 11,
                    marginBottom: 14,
                    lineHeight: 1.5,
                  }}
                >
                  {trop}
                </div>
              )}
              <label
                style={{
                  background: col,
                  color: '#fff',
                  borderRadius: 10,
                  padding: '12px 18px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-block',
                }}
              >
                📷 Choisir une photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={fromFile}
                />
              </label>
              <div style={{ marginTop: 14 }}>
                <button
                  onClick={stop}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255,255,255,.6)',
                    fontSize: 11,
                    cursor: 'pointer',
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
